<template>
  <div
    class="v-modal__backdrop"
    :class="{ [version]: true, [extraBackdropClass]: !!extraBackdropClass }"
    id="vm-backdrop"
    @click="onClickBackdrop"
  >
    <div :class="containerClasses" @click="onClickContainer" id="vm-container">
      <div v-if="title" class="v-modal__header" :class="{ [classType]: true }">
        <h2>{{ title }}</h2>
      </div>
      <div class="v-modal__body"><slot></slot></div>
      <div v-if="actions" class="v-modal__actions">
        <div v-for="(action, idx) of actions" :key="idx">
          <v-button v-bind="action" @click="onClickAction(action)"></v-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import VButton from "@/components/VButton";

export default Vue.component("VModal", {
  props: {
    title: VueTypes.string,
    wide: VueTypes.bool.def(false),
    full: VueTypes.bool.def(false),
    normal: VueTypes.bool.def(true),
    version: VueTypes.oneOf(["old", "new"]).def("old"),
    size: VueTypes.oneOf([
      "regular",
      "wide",
      "full",
      "narrow",
      "square",
      "small"
    ]).def("regular"),
    classType: VueTypes.oneOf([
      "primary",
      "secondary",
      "tertiary",
      "dark",
      "clean"
    ]).def("dark"),
    customContainerSize: VueTypes.shape({
      width: VueTypes.string,
      height: VueTypes.string
    }),
    actions: VueTypes.arrayOf(
      VueTypes.shape({
        label: VueTypes.string.isRequired,
        value: VueTypes.string.isRequired,
        type: VueTypes.string.def("primary"),
        noBorder: VueTypes.bool.def(false),
        inverted: VueTypes.bool.def(false),
        disabled: VueTypes.bool.def(false),
        size: VueTypes.string,
        click: VueTypes.oneOfType([VueTypes.func, VueTypes.string]).isRequired
      })
    ),
    extraBackdropClass: VueTypes.string.def(""),
    extraClass: VueTypes.string
  },

  components: {
    VButton
  },

  data() {
    return {
      isDisabled: false
    };
  },

  computed: {
    containerClasses() {
      return {
        "v-modal__container": true,
        [`v-modal__container--${this.size}`]: true,
        [this.extraClass]: true
      };
    }
  },

  methods: {
    onClickContainer(evt) {
      evt.stopPropagation();
    },

    onClickBackdrop(evt) {
      evt.stopPropagation();
      this.$emit("close");
    },

    onClickAction(action) {
      if (typeof action.click === "string") {
        this.$emit(action.click, action.value);
      } else {
        action.click(action.value);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-modal";

.#{$ns} {
  /*-----For old modal version-----*/
  &__backdrop.old {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;

    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s ease;

    background-color: transparentize($black, 0.2);
    text-align: left;

    .#{$ns} {
      &__container {
        display: flex;
        flex-direction: column;
        transition: all 0.3s ease;
        background-color: $white;
        border: 1px solid $button-dark-color;
        border-radius: 8px;

        &--narrow {
          width: 33%;
          height: 83%;
        }

        &--wide {
          width: 66%;
          height: 83%;
        }

        &--regular {
          width: 50%;
          height: 70%;

          @media screen and (max-width: 1024px) {
            width: 90%;
            height: 90%;
          }
        }

        &--square {
          width: 50%;
          height: 50%;
        }

        &--full {
          width: 90%;
          height: 90%;
        }

        &--small {
          width: 35%;
          height: 30%;

          @media screen and (max-width: 991px) {
            width: 90%;
            height: 40%;
          }
        }
      }

      &__header {
        width: 100%;
        padding: 1.4em 1em;
        color: $white;
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;

        @include mainColorBgPaletteClasses();

        h2 {
          font-size: 20px;
          font-weight: 700;
        }
      }

      &__body {
        flex-grow: 10;
        padding: 1em;
        overflow-y: auto;
        overflow-x: hidden;

        img {
          margin-bottom: 1em;
          max-width: 100%;
        }
      }

      &__actions {
        display: flex;
        justify-content: flex-end;

        padding: 1rem;
        min-height: 4rem;
        border-top: 1px solid #e5e5e5;

        button {
          margin-left: 1rem;
        }
      }
    }
  }

  /*-----For NEW modal version-----*/

  &__backdrop.new {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;

    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s ease;

    background: rgba(31, 31, 31, 0.76);
    text-align: left;

    .#{$ns} {
      &__container {
        display: flex;
        flex-direction: column;
        transition: all 0.3s ease;
        background-color: $white;
        border: 1px solid $button-dark-color;
        border-radius: 8px;
        box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.2);
        text-align: center;
        width: 470px;
        height: auto;
        max-height: 85%;
        padding: 30px;
      }

      &__header {
        width: 100%;
        background: $white;
        color: $main-text-color;
        text-align: center;
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;
        /*padding: 0 0 13px;*/

        h2 {
          font-size: 32px;
          font-weight: 700;
        }
      }

      &__body {
        padding: 30px 0;
        display: flex;
        flex-direction: column;
        flex-grow: 10;
        min-height: 1em;
        text-align: center;

        img {
          margin-bottom: 1em;
          max-width: 100%;
        }
      }

      &__actions {
        display: flex;
        justify-content: flex-end;

        padding: 0;
        border-top: none;

        button {
          margin-left: 1rem;
        }
      }
    }
  }
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .#{$ns}modal__container,
.modal-leave-active .#{$ns}modal__container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
@include mainColorBgPaletteClasses();
</style>
